import { formatCalendarDate } from '../../../Utils/calendarDate';
import { scheduledStoreDate, storeCalendar } from '../../../Utils/storeTimezone';
import { IonButton, IonButtons, IonCol, IonContent, IonDatetime, IonFooter, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup, IonRow, IonTitle, IonToast, IonToolbar } from '@ionic/react';
import { arrowBackOutline, calendarOutline, timeOutline } from 'ionicons/icons';
import React, { useContext, useMemo, useState } from 'react';
import { MessageContext } from '../../../contexts/MessageContext';
import './Agendamento.css';

type Props = {
  closeAction: Function;
  closeActionSimple: Function;
}

// Mapeia dayOfWeek do backend para número JS (0=domingo, 1=segunda, ...)
const DAY_MAP: Record<string, number> = {
  SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3,
  THURSDAY: 4, FRIDAY: 5, SATURDAY: 6,
};

const Agendamento: React.FC<Props> = (props: Props) => {

  const { dadosEmpresa } = useContext(MessageContext);

  const [showToast, setShowToast] = useState(false);
  const [textToast, setTextToast] = useState("");

  const [tipoAgendamento, setTipoAgendamento] = useState("");
  const [dataAgendamento, setDataAgendamento] = useState("");

  // Inicializar hora com minutos arredondados para múltiplo de 10
  const getHoraInicialArredondada = () => {
    const calendar = new Date(`${storeCalendar(dadosEmpresa).dateTime}Z`);
    calendar.setUTCMinutes(Math.ceil(calendar.getUTCMinutes() / 10) * 10);
    return calendar.toISOString().replace(/Z$/, '');
  };

  const [horaAgendamento, setHoraAgendamento] = useState("");

  // Estados para controlar os seletores de data e hora
  const [showPopoverData, setShowPopoverData] = useState(false);
  const [showPopoverHora, setShowPopoverHora] = useState(false);

  // Estados temporários para edição
  const [tempDataAgendamento, setTempDataAgendamento] = useState("");
  const [tempHoraAgendamento, setTempHoraAgendamento] = useState("");


  // Dias da semana que a empresa funciona
  const diasDisponiveis = useMemo(() => {
    const horarios = dadosEmpresa?.horarios;
    if (!horarios || !Array.isArray(horarios)) return new Set<number>();
    return new Set(horarios.map((h: any) => DAY_MAP[h.dayOfWeek]).filter((d: any) => d !== undefined));
  }, [dadosEmpresa]);

  // Retorna todas as faixas de horário do dia selecionado, ordenadas por início
  const getHorariosParaDia = (isoDate: string): any[] => {
    const horarios = dadosEmpresa?.horarios;
    if (!horarios || !Array.isArray(horarios) || !isoDate) return [];

    const dayOfWeekNum = getDayOfWeekLocal(isoDate);
    const dayName = Object.keys(DAY_MAP).find(k => DAY_MAP[k] === dayOfWeekNum);
    if (!dayName) return [];

    return horarios
      .filter((h: any) => h.dayOfWeek === dayName)
      .sort((a: any, b: any) => a.start.localeCompare(b.start));
  };

  const getIntervaloEmMinutos = (horario: any) => {
    const [startH, startM] = horario.start.split(':').map(Number);
    const inicio = startH * 60 + startM;
    const fim = inicio + horario.duration;
    return { inicio, fim };
  };

  const getMinutosValidosParaHora = (isoDate: string, hora: number): number[] => {
    const horarios = getHorariosParaDia(isoDate);
    if (horarios.length === 0) return [];

    const allMinutes = [0, 10, 20, 30, 40, 50];
    const minutosValidos = new Set<number>();

    horarios.forEach((horario: any) => {
      const { inicio, fim } = getIntervaloEmMinutos(horario);
      allMinutes.forEach((minuto) => {
        const totalMinutos = hora * 60 + minuto;
        if (totalMinutos >= inicio && totalMinutos <= fim) {
          minutosValidos.add(minuto);
        }
      });
    });

    return Array.from(minutosValidos).sort((a, b) => a - b);
  };

  // Verifica se uma data está habilitada (dia da semana disponível e com horários ainda válidos)
  const isDateEnabled = (dateString: string) => {
    if (!diasDisponiveis.has(getDayOfWeekLocal(dateString))) return false;

    // Se for hoje, verifica se o horário de funcionamento ainda não encerrou
    const datePart = dateString.split('T')[0];
    const now = storeCalendar(dadosEmpresa);
    const isToday = datePart === now.date;

    if (isToday) {
      const nowTotalMinutes = now.hour * 60 + now.minute;
      const horarios = getHorariosParaDia(dateString);
      if (horarios.length === 0) return false;

      // Hoje só fica habilitado se ao menos uma faixa ainda não encerrou
      return horarios.some((horario: any) => {
        const { fim } = getIntervaloEmMinutos(horario);
        return nowTotalMinutes < fim;
      });
    }

    return true;
  };

  // O seletor representa a data visual da loja, mesmo quando inclui uma hora ISO.
  const getDayOfWeekLocal = (isoDate: string): number =>
    new Date(`${isoDate.substring(0, 10)}T12:00:00Z`).getUTCDay();

  const isHoje = (isoDate: string): boolean =>
    isoDate.substring(0, 10) === storeCalendar(dadosEmpresa).date;

  // Gera os valores de hora disponíveis para o seletor (hourValues do IonDatetime)
  const getHourValues = (isoDate: string): string | undefined => {
    const horarios = getHorariosParaDia(isoDate);
    if (horarios.length === 0) return undefined;

    let hours = Array.from(new Set(
      horarios.flatMap((horario: any) => {
        const { inicio, fim } = getIntervaloEmMinutos(horario);
        const inicioHora = Math.floor(inicio / 60);
        const fimHora = Math.floor(fim / 60);
        const faixa: number[] = [];
        for (let h = inicioHora; h <= fimHora; h++) {
          faixa.push(h % 24);
        }
        return faixa;
      })
    )).sort((a, b) => a - b);

    // Se for hoje, remove horas que já passaram
    if (isHoje(isoDate)) {
      const now = storeCalendar(dadosEmpresa);
      const nowH = now.hour;
      const nowM = now.minute;
      hours = hours.filter(h => {
        if (h > nowH) return true;
        if (h === nowH) {
          // Mantém a hora atual só se ainda houver minutos futuros válidos
          const minutosValidos = getMinutosValidosParaHora(isoDate, h);
          return minutosValidos.some((m) => m >= nowM);
        }
        return false;
      });
    }

    return hours.length > 0 ? hours.join(',') : undefined;
  };

  // Gera os minutos disponíveis de acordo com a hora selecionada e o horário de funcionamento
  const getMinuteValues = (isoDate: string, isoTime: string): string => {
    const horarios = getHorariosParaDia(isoDate);
    if (horarios.length === 0 || !isoTime) return "0,10,20,30,40,50";

    let currentHour = 0;
    if (isoTime.includes('T')) {
      currentHour = parseInt(isoTime.split('T')[1].split(':')[0]);
    } else {
      currentHour = parseInt(isoTime.split(':')[0]);
    }

    let valid = getMinutosValidosParaHora(isoDate, currentHour);

    // Se for hoje e a hora selecionada for a atual, remove minutos que já passaram
    if (isHoje(isoDate)) {
      const now = storeCalendar(dadosEmpresa);
      if (currentHour === now.hour) {
        valid = valid.filter(m => m >= now.minute);
      }
    }

    return valid.length > 0 ? valid.join(',') : "0";
  };

  // Função para formatar a data no padrão brasileiro
  const formatarData = formatCalendarDate;

  // Função para formatar a hora
  function formatarHora(isoTime: string): string {
    if (!isoTime) return "";
    // Extrair a parte da hora do formato ISO (2025-12-01T18:10)
    const timePart = isoTime.includes('T') ? isoTime.split('T')[1] : isoTime;
    const [hora, minuto] = timePart.split(':');
    return `${hora}:${minuto}`;
  }

  return (
    <>

      <IonHeader className="ion-no-border">
        <IonToolbar className="pedido-fluxo-modal-toolbar">
          <IonButtons slot="start" className="pedido-fluxo-back-buttons">
            <IonButton aria-label="Voltar" className="pedido-fluxo-back-button" fill="clear" onClick={() => props.closeActionSimple()}>
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle className="ion-text-wrap"><span className="modal-title-text">Data do Pedido</span></IonTitle>

        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={false}>

        <div className="agendamento-titulo1">
          Quando deseja receber seu pedido?
        </div>

        <IonRadioGroup value={tipoAgendamento} onIonChange={(e: any) => setTipoAgendamento(e.detail.value)}>
          <IonItem lines="none">
            <IonLabel>Agora</IonLabel>
            <IonRadio slot="end" value="AGORA" />
          </IonItem>

          {dadosEmpresa?.agendamento === true && <IonItem lines="none">
            <IonLabel>Agendar pedido</IonLabel>
            <IonRadio slot="end" value="AGENDADO" />
          </IonItem>}
        </IonRadioGroup>

        {tipoAgendamento === "AGENDADO" && (
          <>
            <div className="agendamento-titulo2">
              Selecione a <span className="agendamento-whatsapp-text">data e hora</span> para o agendamento do seu pedido
            </div>

            <IonItem
              lines="none"
              className="input-item"
              button
              onClick={(e) => {
                setTempDataAgendamento(dataAgendamento); // Inicializa estado temporário
                setShowPopoverData(true);
              }}
            >
              <IonIcon slot="start" icon={calendarOutline} />
              <IonInput
                value={dataAgendamento ? formatarData(dataAgendamento) : ""}
                placeholder="Selecione a data"
                readonly
              ></IonInput>
            </IonItem>

            <IonModal
              className="mini-modal agendamento-picker-modal agendamento-data-picker"
              isOpen={showPopoverData}
              onDidDismiss={() => setShowPopoverData(false)}
            >
              <IonHeader className="ion-no-border">
                <IonToolbar><IonTitle><span className="modal-title-text">Data do pedido</span></IonTitle></IonToolbar>
              </IonHeader>
              <IonContent>
                <IonDatetime
                  presentation="date"
                  locale="pt-BR"
                  value={tempDataAgendamento}
                  isDateEnabled={isDateEnabled}
                  onIonChange={(e: any) => {
                    if (e.detail.value) {
                      setTempDataAgendamento(e.detail.value);
                    }
                  }}
                  min={storeCalendar(dadosEmpresa).dateTime}
                  max={storeCalendar(dadosEmpresa, new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)).dateTime}
                />
              </IonContent>

              <IonFooter className="ion-no-border">
                <IonToolbar>
                  <IonButtons slot="end">
                    <IonButton onClick={() => {
                      setTempDataAgendamento(""); // Limpa temporário
                      setShowPopoverData(false);
                    }}>
                      Cancelar
                    </IonButton>
                  </IonButtons>
                  <IonButtons slot="end">
                    <IonButton strong onClick={() => {
                      if (tempDataAgendamento !== dataAgendamento) {
                        setHoraAgendamento(""); // Reseta hora ao trocar de data
                      }
                      setDataAgendamento(tempDataAgendamento); // Confirma mudança
                      setShowPopoverData(false);
                    }}>
                      Confirmar
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonFooter>
            </IonModal>

            <IonItem
              lines="none"
              className="input-item"
              button
              onClick={(e) => {
                // Se já tiver hora, usa ela. Se não, usa a hora sugerida (arredondada)
                setTempHoraAgendamento(horaAgendamento || getHoraInicialArredondada());
                setShowPopoverHora(true);
              }}
            >
              <IonIcon slot="start" icon={timeOutline} />
              <IonInput
                value={horaAgendamento ? formatarHora(horaAgendamento) : ""}
                placeholder="Selecione a hora"
                readonly
              />
            </IonItem>

            <IonModal
              className="mini-modal agendamento-picker-modal agendamento-hora-picker"
              isOpen={showPopoverHora}
              onDidDismiss={() => setShowPopoverHora(false)}
            >
              <IonHeader className="ion-no-border">
                <IonToolbar><IonTitle><span className="modal-title-text">Horário do pedido</span></IonTitle></IonToolbar>
              </IonHeader>
              <IonContent>
                <IonDatetime
                  presentation="time"
                  locale="pt-BR"
                  minuteValues={getMinuteValues(dataAgendamento, tempHoraAgendamento)}
                  hourValues={dataAgendamento ? getHourValues(dataAgendamento) : undefined}
                  value={tempHoraAgendamento}
                  onIonChange={(e: any) => {
                    if (e.detail.value) {
                      setTempHoraAgendamento(e.detail.value);
                    }
                  }}
                />
              </IonContent>

              <IonFooter className="ion-no-border">
                <IonToolbar>
                  <IonButtons slot="end">
                    <IonButton onClick={() => {
                      setTempHoraAgendamento(""); // Limpa temporário
                      setShowPopoverHora(false);
                    }}>
                      Cancelar
                    </IonButton>
                  </IonButtons>
                  <IonButtons slot="end">
                    <IonButton strong onClick={() => {
                      setHoraAgendamento(tempHoraAgendamento); // Confirma mudança
                      setShowPopoverHora(false);
                    }}>
                      Confirmar
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonFooter>
            </IonModal>
          </>
        )}

        <IonToast
          isOpen={showToast}
          position="middle"
          onDidDismiss={() => setShowToast(false)}
          message={textToast}
          duration={3000}
        />

      </IonContent>

      <IonFooter className="ion-no-border">
        <IonToolbar>
          <IonGrid>
            <IonRow>
              <IonCol>
                <IonButton
                  style={{ height: "46px", fontSize: "18px", backgroundColor: "#4CAF50", color: "#fff" }}
                  onClick={async () => {

                    if (!tipoAgendamento) {
                      setTextToast("Selecione uma opção para continuar.");
                      setShowToast(true);
                      return;
                    }

                    if (tipoAgendamento === "AGENDADO") {
                      if (!dataAgendamento) {
                        setTextToast("Selecione a data de entrega.");
                        setShowToast(true);
                        return;
                      }

                      if (!horaAgendamento) {
                        setTextToast("Selecione a hora de entrega.");
                        setShowToast(true);
                        return;
                      }

                      const [hora, minuto] = formatarHora(horaAgendamento).split(':').map(Number);
                      let dataHoraSelecionada: Date;
                      try {
                        dataHoraSelecionada = scheduledStoreDate(dadosEmpresa,
                          dataAgendamento.substring(0, 10), formatarHora(horaAgendamento));
                      } catch (error) {
                        setTextToast(error instanceof Error ? error.message : 'Selecione uma data e hora válidas.');
                        setShowToast(true);
                        return;
                      }
                      const agora = new Date();
                      agora.setSeconds(0, 0);

                      if (dataHoraSelecionada < agora) {
                        setTextToast("A data de agendamento deve ser futura.");
                        setShowToast(true);
                        return;
                      }

                      // Validar se o horário está dentro do funcionamento da empresa
                      const horariosDia = getHorariosParaDia(dataAgendamento);
                      const horarioTotalMinutos = hora * 60 + minuto;

                      const dentroDeAlgumaFaixa = horariosDia.some((horario: any) => {
                        const { inicio, fim } = getIntervaloEmMinutos(horario);
                        return horarioTotalMinutos >= inicio && horarioTotalMinutos <= fim;
                      });

                      if (!dentroDeAlgumaFaixa) {
                        setTextToast("Horário fora do funcionamento para o dia selecionado.");
                        setShowToast(true);
                        return;
                      }

                      props.closeAction(dataAgendamento, horaAgendamento, tipoAgendamento);
                    } else {
                      // Tipo AGORA
                      props.closeAction("", "", tipoAgendamento);
                    }

                  }}
                  expand="block"
                  type="submit"
                >
                  Confirmar
                </IonButton>

              </IonCol>
            </IonRow>
          </IonGrid>
        </IonToolbar>
      </IonFooter>


    </>
  );
};

export default ({ closeAction, closeActionSimple }: { closeAction: Function, closeActionSimple: Function }) => (
  <Agendamento closeAction={closeAction} closeActionSimple={closeActionSimple}>
  </Agendamento>
)
